import {
  findJobsForStep,
  mostRecentMatrixJobs,
  mostRecentParallelJobs,
  useBuild,
} from "~/components/build/Show/lib/BuildContext";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { Drawer } from "~/components/build/Show/components/Drawer";
import { startTransition, useCallback, useEffect } from "react";
import { ErrorBoundary } from "~/lib/Bugsnag";
import { ErrorView } from "~/components/shared/ErrorView";
import {
  BlockStep,
  CommandStep,
  InputStep,
  isMatrixCommandStep,
  isParallelCommandStep,
  Step,
  TriggerStep,
  WaitStep,
} from "~/lib/pipeline";
import {
  Job,
  Build,
  CommandJob,
  InputJob,
  TriggerJob,
  WaitJob,
} from "~/components/build/Show/lib/types";
import { ParallelStepDetails } from "~/components/build/Show/components/StepDrawer/ParallelStepDetails";
import { MatrixStepDetails } from "~/components/build/Show/components/StepDrawer/MatrixStepDetails";
import {
  CommandJobDetails,
  RetryJobNavigation,
} from "~/components/build/Show/components/StepDrawer/CommandJobDetails";
import { TriggerJobDetails } from "~/components/build/Show/components/StepDrawer/TriggerJobDetails";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { GoToFailure } from "~/components/build/Show/components/Sidebar/GoToFailure";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchBuildJob, fetchBuildStepWithJobs } from "~/components/build/Show/lib/queries";
import { BuildStates } from "~/constants/BuildStates";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useHotkeysContext } from "react-hotkeys-hook";
import cable from "~/lib/cable";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";

export const StepDrawer = ({ stepOrJobId = "" }) =>
  Features.ProgressiveBuildShow ? (
    <LazyDataStepDrawer />
  ) : (
    <EagerDataStepDrawer stepOrJobId={stepOrJobId} />
  );

const StepActions = () => {
  return (
    <div className="flex flex-auto">
      <GoToFailure />
    </div>
  );
};

const CommandJobActions = (props: { job: CommandJob; jobWithRetries?: CommandJob[] }) => {
  return (
    <div className="flex flex-auto">
      <GoToFailure />
      <RetryJobNavigation {...props} />
    </div>
  );
};

function StepDrawerSkeleton({ handleClose }: { handleClose: () => void }) {
  return (
    <Drawer onClose={handleClose}>
      <div className="mx-2.5">
        <div className="mb-2 h-[44px] w-full animate-pulse rounded-sm bg-gray-200" />
        <div className="h-64 w-full animate-pulse rounded-sm bg-gray-200" />
      </div>
    </Drawer>
  );
}

const RUNNING_STATES: BuildStates[] = ["failing", "running", "started"];
function isBuildRunning(build: Build) {
  return RUNNING_STATES.includes(build.state);
}

function LazyDataStepDrawer() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { build } = useBuild();
  const { pathname } = useLocation();
  const params = useQueryParams();

  const jobFetchResult = useQuery({
    queryKey: ["build", build.number, "jobs", params.jobUuid],
    queryFn: () =>
      fetchBuildJob({
        buildDataBasePath: build.buildDataBasePath,
        jobUuid: params.jobUuid!,
        buildNumber: build.number,
        queryClient,
      }),
    staleTime: isBuildRunning(build) ? 1000 * 10 : 1000 * 60 * 1, // 10 seconds if build is running, 1 minute if build is not running
    enabled: Boolean(params.jobUuid) && params.open !== "false",
  });

  const stepFetchResult = useQuery({
    queryKey: ["build", build.number, "steps", params.stepUuid, "withJobs"],
    queryFn: () =>
      fetchBuildStepWithJobs({
        buildDataBasePath: build.buildDataBasePath,
        stepUuid: params.stepUuid!,
        buildNumber: build.number,
        queryClient,
      }),
    staleTime: isBuildRunning(build) ? 1000 * 10 : 1000 * 60 * 1, // 10 seconds if build is running, 1 minute if build is not running
    enabled: Boolean(params.stepUuid) && params.open !== "false",
  });

  // Invalidate job queries when annotations change.
  const jobs = stepFetchResult.data?.jobs || [];
  const jobUuid = params.jobUuid || jobs[jobs.length - 1]?.id;

  // Sync jobs to build store
  useEffect(() => {
    const jobs = stepFetchResult.data?.jobs;
    if (jobs) {
      startTransition(() => {
        useBuildStore.getState().setJobs(jobs);
      });
    }
  }, [stepFetchResult.dataUpdatedAt]);

  // Sync job to build store
  useEffect(() => {
    const job = jobFetchResult.data?.job;
    if (job) {
      startTransition(() => {
        useBuildStore.getState().setJobs([job]);
      });
    }
  }, [jobFetchResult.dataUpdatedAt]);

  useEffect(() => {
    if (!jobUuid) {
      return;
    }

    const sub = cable.subscriptions.create(
      { channel: "Pipelines::JobChannel", uuid: jobUuid },
      {
        received({ event }) {
          if (event === "annotations:changed") {
            queryClient.invalidateQueries({
              queryKey: ["build", build.number, "jobs", jobUuid],
              exact: true,
            });
            queryClient.invalidateQueries({
              queryKey: ["build", build.number, "steps", params.stepUuid, "withJobs"],
              exact: true,
            });
          }
        },
      },
    );
    return () => sub.unsubscribe();
  }, [jobUuid, params.stepUuid]);

  // Close the drawer and navigate back to the build view (ie. waterfall / canvas / summary).
  const handleClose = useCallback(() => {
    navigate(pathname);
  }, [pathname]);

  // The trigger/root step isn't a real step in the pipeline so for now we
  // just ignore it.
  if (params.stepUuid === "root") {
    return null;
  }

  if (params.open === "false") {
    return null;
  }

  if (params.jobUuid) {
    if (jobFetchResult.error || Boolean(jobFetchResult.failureReason)) {
      return <Navigate to={pathname} />;
    }

    if (!jobFetchResult.data) {
      return <StepDrawerSkeleton handleClose={handleClose} />;
    }

    if (jobFetchResult.data.job?.type === "script") {
      return (
        <Drawer
          onClose={handleClose}
          actions={
            <CommandJobActions
              job={jobFetchResult.data.job}
              jobWithRetries={jobFetchResult.data.jobWithRetries as CommandJob[]}
            />
          }
        >
          <ErrorBoundary FallbackComponent={ErrorView}>
            <CommandJobDetails job={jobFetchResult.data.job} />
          </ErrorBoundary>
        </Drawer>
      );
    }

    if (jobFetchResult.data.job?.type === "trigger") {
      return (
        <Drawer onClose={handleClose}>
          <ErrorBoundary FallbackComponent={ErrorView}>
            <TriggerJobDetails job={jobFetchResult.data.job} />
          </ErrorBoundary>
        </Drawer>
      );
    }
  }

  if (params.stepUuid) {
    if (stepFetchResult.error || Boolean(stepFetchResult.failureReason)) {
      return <Navigate to={pathname} />;
    }

    if (!stepFetchResult.data) {
      return <StepDrawerSkeleton handleClose={handleClose} />;
    }

    if (stepFetchResult.data.step.type === "trigger") {
      return (
        <Drawer onClose={handleClose} actions={<StepActions />}>
          <ErrorBoundary FallbackComponent={ErrorView}>
            <TriggerJobDetails
              job={stepFetchResult.data.jobs[stepFetchResult.data.jobs.length - 1] as TriggerJob}
            />
          </ErrorBoundary>
        </Drawer>
      );
    }

    // If the step is not a trigger or command, don't show anything
    if (stepFetchResult.data.step.type !== "command") {
      return null;
    }

    if (isParallelCommandStep(stepFetchResult.data.step)) {
      const recentJobs = mostRecentParallelJobs(stepFetchResult.data.jobs as CommandJob[]);
      return (
        <Drawer onClose={handleClose} actions={<StepActions />}>
          <ErrorBoundary FallbackComponent={ErrorView}>
            <ParallelStepDetails step={stepFetchResult.data.step} jobs={recentJobs} />
          </ErrorBoundary>
        </Drawer>
      );
    }

    if (isMatrixCommandStep(stepFetchResult.data.step)) {
      const recentJobs = mostRecentMatrixJobs(stepFetchResult.data.jobs as CommandJob[]);
      return (
        <Drawer onClose={handleClose} actions={<StepActions />}>
          <ErrorBoundary FallbackComponent={ErrorView}>
            <MatrixStepDetails step={stepFetchResult.data.step} jobs={recentJobs} />
          </ErrorBoundary>
        </Drawer>
      );
    }

    const recentJob = stepFetchResult.data.jobs[stepFetchResult.data.jobs.length - 1] as CommandJob;
    return (
      <Drawer
        onClose={handleClose}
        actions={
          <CommandJobActions
            job={recentJob}
            jobWithRetries={stepFetchResult.data.jobs as CommandJob[]}
          />
        }
      >
        <ErrorBoundary FallbackComponent={ErrorView}>
          <CommandJobDetails
            job={recentJob}
            jobWithRetries={stepFetchResult.data.jobs as CommandJob[]}
          />
        </ErrorBoundary>
      </Drawer>
    );
  }

  return null;
}

function EagerDataStepDrawer({ stepOrJobId }: { stepOrJobId: string }) {
  const navigate = useNavigate();
  const { build, store } = useBuild();
  const { stepsMap } = useSteps();
  const { pathname } = useLocation();
  const params = useQueryParams();

  // Close the drawer and navigate back to the build view (ie. waterfall / canvas / summary).
  const handleClose = useCallback(() => {
    navigate(pathname);
  }, [pathname]);

  // The trigger/root step isn't a real step in the pipeline so for now we
  // just ignore it.
  if (stepOrJobId === "root") {
    return null;
  }

  if (params.open === "false") {
    return null;
  }

  let job: Job | null | undefined;

  // If the stepId is a job, render the job details.
  job = store.jobs.get(stepOrJobId);
  if (job?.type === "script") {
    return (
      <Drawer onClose={handleClose} actions={<CommandJobActions job={job} />}>
        <ErrorBoundary FallbackComponent={ErrorView}>
          <CommandJobDetails job={job} />
        </ErrorBoundary>
      </Drawer>
    );
  }

  if (job?.type === "trigger") {
    return (
      <Drawer onClose={handleClose}>
        <ErrorBoundary FallbackComponent={ErrorView}>
          <TriggerJobDetails job={job} />
        </ErrorBoundary>
      </Drawer>
    );
  }

  // Redirect for unknown steps.
  const step = stepsMap.get(stepOrJobId);
  if (!step) {
    if (Features.ProgressiveBuildShow && build.loading) {
      return (
        <Drawer onClose={handleClose}>
          <ErrorBoundary FallbackComponent={ErrorView}>
            <div className="h-64 w-full animate-pulse rounded bg-gray-200" />
          </ErrorBoundary>
        </Drawer>
      );
    }
    return <Navigate to={`${build.path}`} />;
  }

  if (step.type === "trigger") {
    const job = findMostRecentJob(step, build);

    return (
      <Drawer onClose={handleClose} actions={<StepActions />}>
        <ErrorBoundary FallbackComponent={ErrorView}>
          <TriggerJobDetails job={job} />
        </ErrorBoundary>
      </Drawer>
    );
  }

  // Only render the drawer for command steps.
  if (step.type !== "command") {
    return null;
  }

  // Render parallel jobs for parallel command steps.
  if (isParallelCommandStep(step)) {
    return (
      <Drawer onClose={handleClose} actions={<StepActions />}>
        <ErrorBoundary FallbackComponent={ErrorView}>
          <ParallelStepDetails step={step} />
        </ErrorBoundary>
      </Drawer>
    );
  }

  // Render matrix jobs for matrix command steps.
  if (isMatrixCommandStep(step)) {
    return (
      <Drawer onClose={handleClose} actions={<StepActions />}>
        <ErrorBoundary FallbackComponent={ErrorView}>
          <MatrixStepDetails step={step} />
        </ErrorBoundary>
      </Drawer>
    );
  }

  job = findMostRecentJob(step, build);
  if (!job) {
    console.error("No job found for step", step);
    return null;
  }

  return (
    <Drawer onClose={handleClose} actions={<CommandJobActions job={job} />}>
      <ErrorBoundary FallbackComponent={ErrorView}>
        <CommandJobDetails job={job} />
      </ErrorBoundary>
    </Drawer>
  );
}

type StepJob<T extends Step> = T extends CommandStep
  ? CommandJob
  : T extends TriggerStep
    ? TriggerJob
    : T extends InputStep
      ? InputJob
      : T extends BlockStep
        ? InputJob
        : T extends WaitStep
          ? WaitJob
          : never;

function findMostRecentJob<T extends Step>(step: T, build: Build): StepJob<T> {
  const jobs = findJobsForStep(build, step.uuid);
  return jobs[jobs.length - 1] as StepJob<T>;
}
